/**
 * SessionContextPanel — the session's context-window view.
 *
 * Top to bottom: a session line (retained memory + cumulative spend, one line,
 * tooltip-explained); a HEADLINE showing the parent agent's real current usage
 * (one bar with one "X / Y (Z%)" label, or an explicit no-data state — never a
 * fabricated 0%); the in-context TREE (live / superseded / compacted entries);
 * the AGENTS section (active agents with real bars first, inactive agents behind
 * a show/hide toggle showing their real historical spend + final context);
 * compaction history; and the separate Pinned items section. The old "Cached
 * sources" flat list, the "Dedup stats" pill, and the `usagePercent > 0`-as-running
 * sort are all removed.
 *
 * Mounted inside ChatDebugPanel's Context section.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Tooltip } from '@localagent/ui/components/tooltip';
import type { AgentSession } from '../../types/session-types';
import type { ContextSnapshot, SessionContextSummary } from '../../types/context-types';
import type { TicketDetail } from '@localagent/sdk';
import { ContextUsageBar } from './ContextUsageBar';
import { ContextEntryTree } from './ContextEntryTree';
import { TicketContextPanel } from '../work/TicketContextPanel';
import { useTicketForSession } from '../../hooks/useWorkManagement';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { getSDK } from '../../services/sdk-instance';
import { formatPersonaLabel, formatPersonaName } from '../../utils/persona-display';
import { CONTEXT_SESSION_TOOLTIP } from './context-copy';

interface SessionContextPanelProps {
  session: AgentSession;
  sessionId?: string;
  sessionContext: SessionContextSummary | null;
  pinChangeCounter?: number;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

function agentLabel(snap: ContextSnapshot, key: string): string {
  return snap.personaName ? formatPersonaLabel(snap.personaName, key) : key;
}

export function SessionContextPanel({
  session,
  sessionId,
  sessionContext,
  pinChangeCounter: _pinChangeCounter,
}: SessionContextPanelProps) {
  void _pinChangeCounter;
  const [showInactive, setShowInactive] = useState(false);
  const { data: mapping } = useTicketForSession(sessionId);
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const { data: ticketDetail } = useQuery<TicketDetail, Error>({
    queryKey: ['work', 'ticket', mapping?.ticket_id],
    queryFn: () => getSDK().workManagement.getTicket(mapping!.ticket_id),
    enabled: Boolean(isAuthenticated && sdkConfigured && mapping?.ticket_id),
  });

  const pins = session?.session_metadata?.context_pins as Array<{
    item_id: string;
    reason: string;
    estimated_tokens: number;
  }> | undefined;

  const parent = sessionContext?.parentContext ?? null;
  const parentHasUsage = parent?.usagePercent != null && parent?.contextWindowSize != null;

  // Agent rows split by lifecycle status (no usagePercent-as-running hack).
  const allTasks = sessionContext ? Array.from(sessionContext.taskContexts.entries()) : [];
  const activeTasks = allTasks.filter(([, s]) => s.status === 'active');
  const inactiveTasks = allTasks.filter(([, s]) => s.status !== 'active');
  activeTasks.sort((a, b) => a[1].timestamp.localeCompare(b[1].timestamp));
  inactiveTasks.sort((a, b) => a[1].timestamp.localeCompare(b[1].timestamp));

  return (
    <div className="space-y-3">
      {/* Session line — ONE reconciled whole-session summary: memory retained in
          the context store + cumulative provider spend. Tooltip-explained so the
          prose never clutters the panel (session-context-footprint UI). */}
      {((session.context_footprint?.total_tokens ?? 0) > 0 ||
        (sessionContext?.sessionSpendTokens ?? 0) > 0) && (
        <Tooltip label={CONTEXT_SESSION_TOOLTIP} side="bottom">
          <p data-testid="session-memory-stat" className="text-[10px] text-muted-foreground cursor-help">
            <span className="font-medium text-foreground">Session:</span>{' '}
            {session.context_footprint && session.context_footprint.total_tokens > 0 && (
              <span data-testid="session-memory-tokens">{formatTokens(session.context_footprint.total_tokens)} retained</span>
            )}
            {session.context_footprint && session.context_footprint.total_tokens > 0 && (sessionContext?.sessionSpendTokens ?? 0) > 0
              ? ' · '
              : ''}
            {sessionContext && sessionContext.sessionSpendTokens > 0 && (
              <span data-testid="session-spend-tokens">{formatTokens(sessionContext.sessionSpendTokens)} spent</span>
            )}
          </p>
        </Tooltip>
      )}

      {/* Headline — parent agent's current usage. The bar carries the
          single "X / Y (Z%)" label; no separate text line beside it. */}
      {parent && (
        <div
          data-testid="context-headline"
          data-total-tokens={parentHasUsage ? String(parent.promptTokens ?? '') : ''}
        >
          <h3 className="text-[11px] font-medium text-muted-foreground mb-1">
            Context{parent.personaName ? ` — ${formatPersonaName(parent.personaName)} (parent)` : ''}
          </h3>
          {parentHasUsage ? (
            <ContextUsageBar
              usagePercent={parent.usagePercent}
              attribution={parent.attribution ?? undefined}
              contextWindowSize={parent.contextWindowSize ?? undefined}
              promptTokens={parent.promptTokens}
            />
          ) : (
            <p className="text-[10px] text-muted-foreground italic">
              Context usage unavailable for this session
            </p>
          )}
        </div>
      )}

      {/* In-context tree */}
      {parent && (
        <div>
          <h3 className="text-[11px] font-medium text-muted-foreground mb-1.5">In context</h3>
          <ContextEntryTree
            entries={parent.entries}
            supersededEntries={parent.supersededEntries}
            compactedEntries={parent.compactedEntries}
          />
        </div>
      )}

      {/* Agents — active first (real bars), inactive behind a toggle */}
      {allTasks.length > 0 && (
        <div>
          <h3 className="text-[11px] font-medium text-muted-foreground mb-1.5">Agent context windows</h3>
          <div className="space-y-1.5">
            {activeTasks.map(([key, snap]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground truncate min-w-0 max-w-[140px]" title={agentLabel(snap, key)}>
                  {agentLabel(snap, key)}
                </span>
                <div className="flex-1 min-w-0">
                  <ContextUsageBar
                    usagePercent={snap.usagePercent}
                    attribution={snap.attribution ?? undefined}
                    contextWindowSize={snap.contextWindowSize ?? undefined}
                    compact
                  />
                </div>
                <span className="shrink-0 text-[9px] text-green-600 dark:text-green-400">● active</span>
              </div>
            ))}
          </div>

          {/* Inactive agents — collapsed behind a count-bearing toggle */}
          {inactiveTasks.length > 0 && (
            <div className="mt-1.5">
              <button
                type="button"
                data-testid="inactive-agents-toggle"
                data-count={inactiveTasks.length}
                className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                onClick={() => setShowInactive((v) => !v)}
              >
                <span className="inline-block w-2 text-center">{showInactive ? '▾' : '▸'}</span>
                {showInactive ? 'Hide' : 'Show'} {inactiveTasks.length} finished {inactiveTasks.length === 1 ? 'agent' : 'agents'}
              </button>
              {showInactive && (
                <div className="ml-3 mt-0.5 space-y-0.5">
                  {inactiveTasks.map(([key, snap]) => (
                    <div key={key} className="flex items-center justify-between text-[10px] gap-2" data-testid="inactive-agent-row">
                      <span className="text-muted-foreground truncate min-w-0" title={agentLabel(snap, key)}>
                        {agentLabel(snap, key)}
                      </span>
                      <span className="text-muted-foreground whitespace-nowrap shrink-0">
                        {snap.finalUsage
                          ? `used ${formatTokens(snap.finalUsage.totalTokens)} tokens · final context ${formatTokens(snap.finalUsage.contextTotalTokens)} (${Math.round(snap.finalUsage.contextUsagePercent)}%)`
                          : snap.totalTokens != null
                            ? `used ${formatTokens(snap.totalTokens)} tokens`
                            : 'finished'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Compaction history — names folded-entry identities when present */}
      {sessionContext && sessionContext.compactionEvents.length > 0 && (
        <div>
          <h3 className="text-[11px] font-medium text-muted-foreground mb-1.5">Compaction history</h3>
          <div className="space-y-1">
            {sessionContext.compactionEvents.map((event, i) => (
              <div key={i} className="rounded-md bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1 text-[10px] text-amber-700 dark:text-amber-300">
                <div className="inline-flex items-center gap-1.5">
                  <span className="font-medium">{formatPersonaName(event.personaName)}:</span>
                  <span>{event.messagesSummarized} msgs summarized, {formatTokens(event.tokensReduced)} saved</span>
                </div>
                {event.compactedEntries.length > 0 && (
                  <div className="mt-0.5 text-amber-600/80 dark:text-amber-400/70">
                    folded: {event.compactedEntries
                      .map((e) => (e.source_args.path as string) ?? (e.source_args.url as string) ?? e.tool_name)
                      .join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pinned Items — stays a separate section */}
      {pins && pins.length > 0 && (
        <div>
          <h3 className="text-[11px] font-medium text-muted-foreground mb-1.5">Pinned items</h3>
          <div className="space-y-0.5">
            {pins.map((item) => (
              <div
                key={item.item_id}
                className="flex items-center justify-between text-[10px] px-1 py-0.5 rounded hover:bg-accent/30"
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground shrink-0">
                    <line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
                  </svg>
                  <span className="font-mono text-foreground">{item.item_id}</span>
                  <span className="text-muted-foreground truncate">{item.reason}</span>
                </span>
                <span className="text-muted-foreground font-mono shrink-0">{formatTokens(item.estimated_tokens)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pre-loop Context Injection — from context_resolved events */}
      {sessionContext && sessionContext.contextResolved && sessionContext.contextResolved.some(r => r.ctx_injection_breakdown && Object.keys(r.ctx_injection_breakdown).length > 0) && (
        <div>
          <h3 className="text-[11px] font-medium text-muted-foreground mb-1.5">Pre-loop context</h3>
          <div className="space-y-1">
            {sessionContext.contextResolved.map((resolved, i) => {
              const breakdown = resolved.ctx_injection_breakdown;
              if (!breakdown || Object.keys(breakdown).length === 0) return null;
              return (
                <div key={i} className="rounded-md bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 text-[10px] text-blue-700 dark:text-blue-300">
                  <span className="font-medium">{formatPersonaName(resolved.persona_name)}:</span>
                  <span className="ml-1.5">
                    {Object.entries(breakdown).map(([cat, count], j) => (
                      <span key={cat}>
                        {j > 0 && ' · '}
                        {count} {cat.replace('ctx:', '')}
                      </span>
                    ))}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Linked Ticket */}
      {ticketDetail ? (
        <TicketContextPanel ticket={ticketDetail.ticket} />
      ) : (
        !parent && (
          <p className="text-[10px] text-muted-foreground text-center py-4">
            {sessionId ? 'No linked ticket' : 'No active session'}
          </p>
        )
      )}
    </div>
  );
}
